import React, { useState, useEffect } from 'react';
import ApiService from '../../services/apiService';
import './SliderManager.css';

const SliderManager = () => {
  const [sliderImages, setSliderImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingImage, setEditingImage] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    caption: '',
    alt_text: '',
    display_order: '',
    image: null
  });

  useEffect(() => {
    loadSliderImages();
  }, []);

  const loadSliderImages = async () => {
    try {
      setLoading(true);
      const response = await ApiService.getAllSliderImages();
      if (response.success) {
        setSliderImages(response.data);
      } else {
        setError('Failed to load slider images');
      }
    } catch (error) {
      console.error('Error loading slider images:', error);
      setError('Error loading slider images');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      const submitData = new FormData();
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== '') {
          submitData.append(key, formData[key]);
        }
      });

      let response;
      if (editingImage) {
        response = await ApiService.updateSliderImage(editingImage.id, Object.fromEntries(submitData));
      } else {
        response = await ApiService.createSliderImage(Object.fromEntries(submitData));
      }

      if (response.success) {
        await loadSliderImages();
        resetForm();
        setError('');
      } else {
        setError(response.message || 'Failed to save slider image');
      }
    } catch (error) {
      console.error('Error saving slider image:', error);
      setError('Error saving slider image');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this slider image?')) {
      return;
    }

    try {
      setLoading(true);
      const response = await ApiService.deleteSliderImage(id);
      if (response.success) {
        await loadSliderImages();
        setError('');
      } else {
        setError('Failed to delete slider image');
      }
    } catch (error) {
      console.error('Error deleting slider image:', error);
      setError('Error deleting slider image');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      const response = await ApiService.toggleSliderImageStatus(id);
      if (response.success) {
        await loadSliderImages();
        setError('');
      } else {
        setError('Failed to toggle slider image status');
      }
    } catch (error) {
      console.error('Error toggling status:', error);
      setError('Error toggling status');
    }
  };

  const handleEdit = (image) => {
    setEditingImage(image);
    setFormData({
      title: image.title,
      caption: image.caption || '',
      alt_text: image.alt_text || '',
      display_order: image.display_order || '',
      image: null
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      caption: '',
      alt_text: '',
      display_order: '',
      image: null
    });
    setEditingImage(null);
    setShowForm(false);
  };

  const handleInputChange = (e) => {
    const { name, value, type, files } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'file' ? files[0] : value
    }));
  };

  const moveImage = async (id, direction) => {
    const currentIndex = sliderImages.findIndex(img => img.id === id);
    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    
    if (targetIndex < 0 || targetIndex >= sliderImages.length) return;

    try {
      const currentImage = sliderImages[currentIndex];
      const targetImage = sliderImages[targetIndex];
      
      // Swap display orders
      await ApiService.updateSliderImageOrder(currentImage.id, targetImage.display_order);
      await ApiService.updateSliderImageOrder(targetImage.id, currentImage.display_order);
      
      await loadSliderImages();
    } catch (error) {
      console.error('Error reordering images:', error);
      setError('Error reordering images');
    }
  };

  if (loading && sliderImages.length === 0) {
    return <div className="slider-manager-loading">Loading slider images...</div>;
  }

  return (
    <div className="slider-manager">
      {error && (
        <div className="slider-manager-error">
          {error}
          <button onClick={() => setError('')} className="error-close">×</button>
        </div>
      )}

      <div className="slider-manager-header">
        <h3>Slider Images</h3>
        <button 
          onClick={() => setShowForm(!showForm)}
          className="add-slider-btn"
        >
          {showForm ? 'Cancel' : 'Add New Image'}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="slider-form">
          <div className="form-group">
            <label htmlFor="title">Title *</label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="caption">Caption</label>
            <textarea
              id="caption"
              name="caption"
              value={formData.caption}
              onChange={handleInputChange}
              rows="3"
            />
          </div>

          <div className="form-group">
            <label htmlFor="alt_text">Alt Text</label>
            <input
              type="text"
              id="alt_text"
              name="alt_text"
              value={formData.alt_text}
              onChange={handleInputChange}
            />
          </div>

          <div className="form-group">
            <label htmlFor="display_order">Display Order</label>
            <input
              type="number"
              id="display_order"
              name="display_order"
              value={formData.display_order}
              onChange={handleInputChange}
              min="0"
            />
          </div>

          <div className="form-group">
            <label htmlFor="image">
              Image {!editingImage && '*'}
            </label>
            <input
              type="file"
              id="image"
              name="image"
              onChange={handleInputChange}
              accept="image/*"
              required={!editingImage}
            />
            {editingImage && (
              <small>Leave empty to keep current image</small>
            )}
          </div>

          <div className="form-actions">
            <button type="submit" disabled={loading}>
              {loading ? 'Saving...' : (editingImage ? 'Update' : 'Create')}
            </button>
            <button type="button" onClick={resetForm}>
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="slider-list">
        {sliderImages.length === 0 ? (
          <p className="no-sliders">No slider images found. Add some images to get started.</p>
        ) : (
          sliderImages.map((image, index) => (
            <div key={image.id} className={`slider-item ${!image.is_active ? 'inactive' : ''}`}>
              <div className="slider-item-image">
                <img 
                  src={image.image_url} 
                  alt={image.alt_text} 
                  onError={(e) => {
                    e.target.src = '/sliderImages/placeholder.jpg';
                  }}
                />
              </div>
              
              <div className="slider-item-content">
                <h4>{image.title}</h4>
                {image.caption && <p>{image.caption}</p>}
                <div className="slider-item-meta">
                  <span>Order: {image.display_order}</span>
                  <span className={`status ${image.is_active ? 'active' : 'inactive'}`}>
                    {image.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>

              <div className="slider-item-actions">

                <div className="action-buttons">
                  <button 
                    onClick={() => handleDelete(image.id)}
                    className="delete-btn"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default SliderManager;